import { useState, useMemo, useRef, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Download, Loader2, X, Archive } from 'lucide-react';
import { createPortal } from 'react-dom';
import toast from 'react-hot-toast';
import { getCampaignAssets } from '@/api/campaigns';
import { getSearchAssets } from '@/api/searchAssets';
import { authenticatedFetch } from '@/api/client';

// Stagger between sequential downloads — keeps peak memory at one ZIP and avoids
// the browser's "multiple file download" auto-block.
const STAGGER_MS = 500;
const sleep = (ms: number) => new Promise<void>((resolve) => setTimeout(resolve, ms));

async function downloadZip(campaignId: string, variantNumber: number) {
  const url = `/api/campaigns/${campaignId}/variants/${variantNumber}/download`;
  const res = await authenticatedFetch(url);
  if (!res.ok) {
    throw new Error(`Download failed: ${res.statusText}`);
  }
  const blob = await res.blob();
  const a = document.createElement('a');
  a.href = URL.createObjectURL(blob);
  a.download = `variant-${variantNumber}-assets.zip`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(a.href);
}

const SLOT_LABELS: Record<string, string> = {
  image: 'Image',
  landscape_image: 'Landscape Image',
  square_image: 'Square Image',
  portrait_image: 'Portrait Image',
  square_logo: 'Square Logo',
  landscape_logo: 'Landscape Logo',
  video: 'Video',
  companion_banner: 'Companion Banner',
  image_extension: 'Image Extension',
};

interface DownloadAsset {
  id: string;
  campaignId: string;
  filename: string;
  label: string;
}

interface Variant {
  label: string;
  variantNumber: number;
  assets: DownloadAsset[];
}

function useDownloadData(
  campaignId: string,
  isSearch: boolean,
): { variants: Variant[]; isLoading: boolean } {
  const { data: socialData, isLoading: socialLoading } = useQuery({
    queryKey: ['campaign-assets', campaignId],
    queryFn: () => getCampaignAssets(campaignId, true),
    enabled: !isSearch,
  });

  const { data: searchData, isLoading: searchLoading } = useQuery({
    queryKey: ['search-assets', campaignId],
    queryFn: () => getSearchAssets(campaignId, true),
    enabled: isSearch,
  });

  const variants = useMemo(() => {
    if (isSearch && searchData?.assets) {
      const variantNumbers = [...new Set(searchData.assets.map((a) => a.variant_number))].sort(
        (a, b) => a - b,
      );
      return variantNumbers.map((vn) => {
        const asset = searchData.assets.find((a) => a.variant_number === vn);
        const files = (asset?.media_files ?? [])
          .filter((mf) => mf.slot !== 'youtube_video' && (mf.signed_url || mf.storage_path))
          .map((mf, idx) => {
            const originalName = (mf.storage_path || '').split('/').pop() || `file-${idx + 1}`;
            const slotLabel = SLOT_LABELS[mf.slot] ?? mf.slot.replace(/_/g, ' ');
            return {
              id: mf.id,
              campaignId,
              filename: `${String(idx + 1).padStart(2, '0')}_${originalName}`,
              label: slotLabel,
            };
          });
        return { label: `Variant ${vn}`, variantNumber: vn, assets: files };
      });
    }

    if (!isSearch && socialData?.assets) {
      const variantNumbers = [...new Set(socialData.assets.map((a) => a.variant_number ?? 1))].sort(
        (a, b) => a - b,
      );
      return variantNumbers.map((vn) => {
        const files = socialData.assets
          .filter((a) => (a.variant_number ?? 1) === vn && a.storage_path)
          .map((a, idx) => {
            const originalName = (a.storage_path || '').split('/').pop() || `file-${idx + 1}`;
            return {
              id: a.id,
              campaignId,
              filename: `${String(idx + 1).padStart(2, '0')}_${originalName}`,
              label: a.ratio ? `${a.ratio} Image` : `Image ${idx + 1}`,
            };
          });
        return { label: `Variant ${vn}`, variantNumber: vn, assets: files };
      });
    }

    return [];
  }, [isSearch, searchData, socialData, campaignId]);

  return { variants, isLoading: isSearch ? searchLoading : socialLoading };
}

export function DownloadAssetsModal({
  campaignId,
  isSearch,
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  nameplate: _nameplate,
  onClose,
}: {
  campaignId: string;
  isSearch: boolean;
  nameplate?: string;
  onClose: () => void;
}) {
  const [isDownloading, setIsDownloading] = useState(false);
  // Track selection by variant *number* (not array index) so it survives a refetch
  // that reorders the variants list.
  const [selected, setSelected] = useState<Set<number>>(new Set());
  const [progress, setProgress] = useState<{ done: number; total: number } | null>(null);
  const selectAllRef = useRef<HTMLInputElement>(null);
  const { variants, isLoading } = useDownloadData(campaignId, isSearch);

  // ── Derived selection state ───────────────────────────────────────────────
  const selectedCount = selected.size;
  const allSelected = variants.length > 0 && selectedCount === variants.length;
  const someSelected = selectedCount > 0 && !allSelected;
  // Preserve list order when running downloads, regardless of click order.
  const orderedSelected = variants
    .filter((v) => selected.has(v.variantNumber))
    .map((v) => v.variantNumber);

  // Auto-select the single variant in a one-variant campaign (default is none selected).
  // Guarded so it only fires once on initial load — without this, a React Query
  // refetch (e.g. refetch-on-focus) produces a new `variants` reference and would
  // re-select the variant, clobbering a user's manual uncheck.
  const didAutoSelectSingleVariantRef = useRef(false);
  useEffect(() => {
    if (variants.length === 1 && !didAutoSelectSingleVariantRef.current) {
      setSelected(new Set([variants[0].variantNumber]));
      didAutoSelectSingleVariantRef.current = true;
    }
  }, [variants]);

  // Native checkbox indeterminate state can only be set imperatively.
  useEffect(() => {
    if (selectAllRef.current) {
      selectAllRef.current.indeterminate = someSelected;
    }
  }, [someSelected]);

  // ── Handlers ──────────────────────────────────────────────────────────────
  function toggleVariant(variantNumber: number) {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(variantNumber)) {
        next.delete(variantNumber);
      } else {
        next.add(variantNumber);
      }
      return next;
    });
  }

  function toggleSelectAll() {
    setSelected((prev) =>
      prev.size === variants.length ? new Set() : new Set(variants.map((v) => v.variantNumber)),
    );
  }

  // Download each variant as its own ZIP, sequentially and staggered. Per-variant
  // failures are collected (never abort the rest); returns the failed variant numbers.
  // The loading toast (toastId) and the footer progress are refreshed after each
  // variant so the popup reflects live progress instead of jumping to the end.
  async function runSequentialDownloads(
    variantNumbers: number[],
    toastId: string,
  ): Promise<number[]> {
    const failed: number[] = [];
    const total = variantNumbers.length;
    for (let i = 0; i < total; i++) {
      const vn = variantNumbers[i];
      try {
        await downloadZip(campaignId, vn);
      } catch {
        failed.push(vn);
      }
      const done = i + 1;
      setProgress({ done, total });
      if (total > 1) {
        toast.loading(`Downloading ${done}/${total}…`, { id: toastId });
      }
      if (i < total - 1) {
        await sleep(STAGGER_MS);
      }
    }
    return failed;
  }

  async function handleDownload() {
    const targets = orderedSelected;
    if (targets.length === 0) return;

    const total = targets.length;
    setIsDownloading(true);
    setProgress({ done: 0, total });
    const toastId = toast.loading(total > 1 ? `Downloading 0/${total}…` : 'Downloading…');

    try {
      const failed = await runSequentialDownloads(targets, toastId);
      const failedCount = failed.length;
      const okCount = total - failedCount;

      if (failedCount === 0) {
        toast.success(
          total > 1 ? `${total} variant ZIPs downloaded` : `Variant ${targets[0]} ZIP downloaded`,
          { id: toastId },
        );
      } else if (okCount === 0) {
        toast.error(
          total > 1 ? `All ${total} downloads failed` : 'Failed to download variant assets',
          { id: toastId },
        );
      } else {
        toast.error(`${okCount} of ${total} downloaded, ${failedCount} failed`, { id: toastId });
      }
    } finally {
      setIsDownloading(false);
      setProgress(null);
    }
  }

  return createPortal(
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40"
      onClick={isDownloading ? undefined : onClose}
    >
      <div
        className="w-[480px] max-h-[80vh] bg-white rounded-xl border border-slate-200 shadow-xl flex flex-col overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="px-6 py-3 border-b border-slate-200 bg-slate-50 rounded-t-xl flex items-center justify-between flex-shrink-0">
          <div className="flex items-center gap-2">
            <Download className="w-4 h-4 text-slate-500" />
            <p className="text-xs font-semibold uppercase tracking-wider text-slate-600">
              Download Assets
            </p>
          </div>
          <button
            onClick={() => {
              if (!isDownloading) onClose();
            }}
            disabled={isDownloading}
            className="w-6 h-6 flex items-center justify-center rounded-md text-slate-400 hover:text-slate-600 hover:bg-slate-100 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto px-6 py-5">
          {isLoading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-6 h-6 text-slate-400 animate-spin" />
            </div>
          ) : variants.length === 0 ? (
            <div className="flex items-center gap-2 px-3 py-2 rounded-lg bg-blue-50 border border-blue-100">
              <Archive className="w-4 h-4 text-blue-500 shrink-0" />
              <p className="text-xs text-blue-700">
                No variants found for this campaign — nothing to download yet
              </p>
            </div>
          ) : (
            <div className="space-y-1">
              <div className="flex items-center gap-2 px-3 py-2 mb-2 rounded-lg bg-blue-50 border border-blue-100">
                <Archive className="w-4 h-4 text-blue-500 shrink-0" />
                <p className="text-xs text-blue-700">
                  Each selected variant downloads as its own separate ZIP, including text copy as
                  CSV
                </p>
              </div>

              {variants.length > 1 && (
                <label className="flex items-center gap-3 px-3 py-2 rounded-lg cursor-pointer hover:bg-slate-50">
                  <input
                    ref={selectAllRef}
                    type="checkbox"
                    aria-label="Select all variants"
                    checked={allSelected}
                    onChange={toggleSelectAll}
                    disabled={isDownloading}
                    className="w-4 h-4 rounded border-slate-300 accent-[#0014cc] focus:ring-[#0014cc]/30"
                  />
                  <span className="text-sm font-medium text-slate-700">Select all</span>
                </label>
              )}

              {variants.map((v) => (
                <label
                  key={v.variantNumber}
                  htmlFor={`variant-${v.variantNumber}`}
                  className="flex items-center gap-3 px-3 py-2 rounded-lg border border-slate-100 bg-slate-50/50 cursor-pointer hover:bg-slate-50"
                >
                  <input
                    id={`variant-${v.variantNumber}`}
                    type="checkbox"
                    checked={selected.has(v.variantNumber)}
                    onChange={() => toggleVariant(v.variantNumber)}
                    disabled={isDownloading}
                    className="w-4 h-4 rounded border-slate-300 accent-[#0014cc] focus:ring-[#0014cc]/30"
                  />
                  <div className="min-w-0 flex-1">
                    <p className="text-sm text-slate-700 truncate">{v.label}</p>
                    <p className="text-xs text-slate-400">
                      {v.assets.length > 0
                        ? `${v.assets.length} file${v.assets.length === 1 ? '' : 's'}`
                        : '0 media files (text copy CSV only)'}
                    </p>
                  </div>
                </label>
              ))}
            </div>
          )}
        </div>

        <div className="px-6 py-4 border-t border-slate-200 flex justify-end gap-2 flex-shrink-0">
          <button
            onClick={onClose}
            disabled={isDownloading}
            className="px-4 py-2 text-sm font-medium text-slate-600 bg-white border border-slate-200 rounded-lg hover:bg-slate-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Cancel
          </button>
          <button
            onClick={handleDownload}
            disabled={isDownloading || isLoading || selectedCount === 0}
            className="px-4 py-2 text-sm font-medium text-white bg-[#0014cc] rounded-lg hover:bg-[#0014cc]/90 transition-colors disabled:opacity-50 flex items-center gap-2"
          >
            {isDownloading ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" />
                Downloading {progress?.done ?? 0}/{progress?.total ?? selectedCount}…
              </>
            ) : (
              <>
                <Download className="w-4 h-4" />
                {selectedCount >= 2 ? `Download Selected (${selectedCount})` : 'Download ZIP'}
              </>
            )}
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
}
